import {
  useDataContext,
  useFxWorkspaceContext,
  type FxWorkspace,
  type FxWorkspaceLoadStatus,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { Accessor, createMemo } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';

import {
  applyFxReferencesFilter,
  type FxReferencesFilterMode,
} from './filter-fx-reference-nodes';
import {
  buildFxWorkspaceNodesForActive,
  FxWorkspaceProjectData,
  FxWorkspaceTreeItem,
} from './fx-workspace-tree-nodes';
import { isProjectDataLoadItemVisible } from './project-data-load-control';

type ChildrenLoadStatus = 'pending' | 'success' | 'error';

const getFxWorkspacesChildrenLoadStatus = (
  status: FxWorkspaceLoadStatus
): ChildrenLoadStatus => {
  switch (status) {
    case 'idle':
    case 'pending':
      return 'pending';
    case 'error':
      return 'error';
    case 'success':
      return 'success';
    default:
      throw new UnreachableCaseError(status);
  }
};

export interface UseActiveFxWorkspaceTreeOptions {
  referencesFilterMode?: Accessor<FxReferencesFilterMode>;
}

export interface UseActiveFxWorkspaceTreeResult {
  activeWorkspace: Accessor<FxWorkspace | undefined>;
  nodes: Accessor<TreeNodeDefinition<FxWorkspaceTreeItem>[]>;
  childrenLoadStatus: Accessor<ChildrenLoadStatus>;
}

export function useActiveFxWorkspaceTree(
  options: UseActiveFxWorkspaceTreeOptions = {}
): UseActiveFxWorkspaceTreeResult {
  const fxWorkspace = useFxWorkspaceContext();
  const { dataState } = useDataContext();

  const projectData = createMemo((): FxWorkspaceProjectData => {
    const missions =
      dataState.mission.state === LoadState.LoadComplete
        ? Object.values(dataState.mission.items)
        : [];
    const trajectories =
      dataState.trajectory.state === LoadState.LoadComplete
        ? Object.values(dataState.trajectory.items)
        : [];
    const models =
      dataState.tcFileFolder.state === LoadState.LoadComplete
        ? Object.values(dataState.tcFileFolder.items)
        : [];
    const pointClouds =
      dataState.pointCloud.state === LoadState.LoadComplete
        ? Object.values(dataState.pointCloud.items)
        : [];

    return { missions, trajectories, models, pointClouds };
  });

  const activeWorkspace = createMemo(() => {
    const activeWorkspaceId = fxWorkspace.activeWorkspaceId();
    if (activeWorkspaceId === undefined) {
      return undefined;
    }

    return fxWorkspace
      .workspaces()
      .find((workspace) => workspace.id === activeWorkspaceId);
  });

  const nodes = createMemo(() => {
    const workspace = activeWorkspace();
    if (workspace === undefined) {
      return [];
    }

    const tree = buildFxWorkspaceNodesForActive(workspace, projectData(), {
      getWorkspaceBreaklines: (workspaceId) =>
        fxWorkspace.listWorkspaceBreaklines(workspaceId),
      isBreaklinesBuilding: (workspaceId) =>
        fxWorkspace.isBreaklinesBuilding(workspaceId),
    });
    const filterMode = options.referencesFilterMode?.() ?? 'all';

    return applyFxReferencesFilter(tree, filterMode, (node) => {
      if (node.data?.kind !== 'reference' || node.data.refTrn === undefined) {
        return false;
      }

      switch (filterMode) {
        case 'all':
          return true;
        case 'active':
          return (
            node.data.loadItemType !== undefined &&
            isProjectDataLoadItemVisible(
              dataState,
              node.data.loadItemType,
              node.data.refTrn
            )
          );
        case 'workspace':
          return fxWorkspace.isReferenceVisible(workspace.id, node.data.refTrn);
        default:
          throw new UnreachableCaseError(filterMode);
      }
    });
  });

  const childrenLoadStatus = createMemo(() => {
    const listStatus = getFxWorkspacesChildrenLoadStatus(
      fxWorkspace.loadStatus()
    );
    if (listStatus !== 'success') {
      return listStatus;
    }

    if (fxWorkspace.activeWorkspaceId() === undefined) {
      // Auto-activation of the first workspace happens after the list
      // succeeds. Report pending so the tab shows a spinner instead of empty.
      return fxWorkspace.workspaces().length > 0 ? 'pending' : 'success';
    }

    return getFxWorkspacesChildrenLoadStatus(
      fxWorkspace.activeWorkspaceDetailsLoadStatus()
    );
  });

  return {
    activeWorkspace,
    nodes,
    childrenLoadStatus,
  };
}
